import { filter, first, groupBy } from 'lodash'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useTranslations } from '@/lib/next-shim'
import { Speaker } from 'react-feather'
import { MicrophoneIcon, VideoCameraIcon } from '@heroicons/react/24/outline'
import { Image } from '@/lib/next-shim'

import { XMarkIcon } from '@heroicons/react/20/solid'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import Container from '@/components/ui/layouts/Container'

export type DeviceProps = {
  id: string
  label: string
  iconUrl?: string
  isSelected: boolean
  type: MediaDeviceKind
}

export type DeviceSelectionDialogProps = {
  /**
   * The device list
   */
  devices?: DeviceProps[]
  onClose: () => void
  /**
   * Callback fired when a device is selected
   */
  onSelectDevice?: (deviceId: string, deviceType: MediaDeviceKind) => void
  open: boolean
  /**
   * if `true`, audio input setting is shown
   * @default true
   */
  showAudioInput?: boolean
  /**
   * if `true`, audio ouput setting is shown
   * @default true
   */
  showAudioOutput?: boolean
  /**
   * if `true`, video input setting is shown
   * @default true
   */
  showVideoInput?: boolean
  /**
   * The title content
   * @default Devices
   */
  title?: string
}

export function DeviceSelectionDialog(props: DeviceSelectionDialogProps) {
  const { devices = [], onClose, onSelectDevice, open, showAudioInput = true, showAudioOutput = true, showVideoInput = true, title } = props
  const t = useTranslations()
  const displayTitle = title ?? t('dialog.DeviceSelection.title')

  const deviceSettings: { name: MediaDeviceKind; icon: React.ReactNode; label: string; show: boolean }[] = useMemo(
    () => [
      {
        name: 'audioinput',
        icon: <MicrophoneIcon className="h-5 w-5" />,
        label: t('dialog.DeviceSelection.mediaDeviceKind.audioinput'),
        show: showAudioInput,
      },
      {
        name: 'audiooutput',
        icon: <Speaker className="h-5 w-5" />,
        label: t('dialog.DeviceSelection.mediaDeviceKind.audiooutput'),
        show: showAudioOutput,
      },
      {
        name: 'videoinput',
        icon: <VideoCameraIcon className="h-5 w-5" />,
        label: t('dialog.DeviceSelection.mediaDeviceKind.videoinput'),
        show: showVideoInput,
      },
    ],
    [showAudioInput, showAudioOutput, showVideoInput, t]
  )
  const [selectedDeviceType, setSelectedDeviceType] = useState<MediaDeviceKind | undefined>(
    first(filter(deviceSettings, (deviceSetting) => deviceSetting.show))?.name
  )

  const groupedByTypeDevices = useMemo(() => groupBy(devices, 'type'), [devices])

  const handleSelectDeviceType = (type: MediaDeviceKind) => () => {
    setSelectedDeviceType(type)
  }

  const handleSelectDevice = (deviceId: string, deviceType: MediaDeviceKind) => {
    onSelectDevice?.(deviceId, deviceType)
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
      scroll="paper"
    >
      <div className="grid grid-cols-12 flex-1 divide-x divide-gray-300">
        <div className="col-span-5 min-[480px]:col-span-4 sm:col-span-3">
          <DialogTitle>{displayTitle}</DialogTitle>
          <ul className="w-full ds-menu ds-menu-compact 2xl:ds-menu-normal bg-base-100 ds-p-2 p-2 rounded-box space-y-1">
            {deviceSettings.map(
              (deviceSetting) =>
                deviceSetting.show && (
                  <li key={deviceSetting.name} className="max-w-full">
                    <a
                      className={clsx('w-full flex items-start gap-2 max-sm:pl-2 max-sm:pr-2', { 'ds-active': selectedDeviceType === deviceSetting.name })}
                      onClick={handleSelectDeviceType(deviceSetting.name as MediaDeviceKind)}
                    >
                      <div className="flex">{deviceSetting.icon}</div>
                      <span className="truncate">{deviceSetting.label}</span>
                    </a>
                  </li>
                )
            )}
          </ul>
        </div>
        <div className="col-span-7 min-[480px]:col-span-8 sm:col-span-9">
          <DialogActions>
            <button className="flex text-gray-400 hover:text-gray-500" onClick={onClose}>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </DialogActions>
          <DialogContent>
            <Container className="max-w-xl px-0">
              <div className="flex flex-col space-y-4">
                {!!selectedDeviceType &&
                  groupedByTypeDevices[selectedDeviceType]?.map((device) => (
                    <button
                      key={`${device.id} - ${device.type}`}
                      className={clsx(
                        'h-auto ds-btn ds-btn-outline ds-btn-primary pt-2 pb-2 border-gray-300 hover:!border-gray-300 hover:!bg-transparent hover:!text-primary',
                        {
                          'border-2 border-indigo-500 hover:!border-indigo-500': device.isSelected,
                          'flex flex-col flex-nowrap hover:!bg-transparent': !!device.iconUrl,
                        }
                      )}
                      onClick={() => handleSelectDevice(device.id, device.type)}
                    >
                      {device.label}
                      {device.iconUrl && <Image src={device.iconUrl} alt={device.label} className="max-w-[144px] w-full h-auto" />}
                    </button>
                  ))}
              </div>
            </Container>
          </DialogContent>
        </div>
      </div>
    </Dialog>
  )
}
